import { EMPTY_SERVICE_SELL_PRICE, Service, ServiceSellPrice, DEFAULT_CURRENCY } from "@turizi/shared";
import { useTranslation } from 'react-i18next';
import { Button, Switch } from '@mantine/core';
import { MoneyNumberInput } from '../snippets';
import { useState, useMemo, useEffect } from 'react';
import { useCreateServiceSellPrice } from "@/hooks/api-hooks";
import { notify } from "@/lib/notify";
import { IconBasketDollar, IconCalendar, IconPlus } from "@tabler/icons-react";
import CurrencySelector from "../currency-selector";
import { DatePickerInput } from "@mantine/dates";
import dayjs from "dayjs";
import CardModal from "../card-modal";

interface ServiceNewPriceProps {
    service: Service;
    lastPriceEndDay?: string | null;
    minDate?: string | null;
}

export default function ServiceNewPrice({
    service,
    lastPriceEndDay,
    minDate,
}: ServiceNewPriceProps) {
    const { t, i18n } = useTranslation();
    const [opened, setOpened] = useState(false);
    const [applyToLinkedService, setApplyToLinkedService] = useState(false);
    const [showForeignerPrice, setShowForeignerPrice] = useState(false);

    const resolvedLastPriceEndDay = useMemo(() => {
        if (lastPriceEndDay !== undefined) return lastPriceEndDay;
        if (minDate !== undefined) return minDate;
        const prices = [...(service.sell_prices || [])].filter(p => p.end_day || p.start_day);
        if (prices.length === 0) return null;
        prices.sort((a, b) => {
            const dateA = a.end_day || a.start_day || '';
            const dateB = b.end_day || b.start_day || '';
            return dateB.localeCompare(dateA);
        });
        return prices[0]?.end_day || null;
    }, [lastPriceEndDay, minDate, service.sell_prices]);

    const [draft, setDraft] = useState<ServiceSellPrice>(() => ({
        ...EMPTY_SERVICE_SELL_PRICE,
        start_day: resolvedLastPriceEndDay || '',
        business_id: null,
    }));

    useEffect(() => {
        if (!opened) {
            setDraft({
                ...EMPTY_SERVICE_SELL_PRICE,
                start_day: resolvedLastPriceEndDay || '',
                business_id: null,
            });
        }
    }, [resolvedLastPriceEndDay, opened]);

    const createPriceMutation = useCreateServiceSellPrice();

    const open = () => {
        setDraft({
            ...EMPTY_SERVICE_SELL_PRICE,
            start_day: resolvedLastPriceEndDay || '',
            business_id: null,
        });
        setShowForeignerPrice(false);
        setApplyToLinkedService(false);
        setOpened(true);
    };

    const close = () => {
        setOpened(false);
        setApplyToLinkedService(false);
        setShowForeignerPrice(false);
        setDraft({
            ...EMPTY_SERVICE_SELL_PRICE,
            start_day: resolvedLastPriceEndDay || '',
            business_id: null,
        });
    };

    const save = async () => {
        if (!service?.id) return;

        const shouldApplyToLinked = Boolean(service.linked_service_id && applyToLinkedService);

        createPriceMutation.mutate({
            ...draft,
            business_id: null,
            service_id: service.id,
            apply_to_linked_service: shouldApplyToLinked,
        }, {
            onSuccess: () => {
                notify.success(
                    shouldApplyToLinked ? t('saved_to_linked_service') : t('price_saved_successfully'),
                    ''
                );
                close();
            }
        });
    };

    return (
        <>
            <Button
                size="xs"
                radius="sm"
                color="black"
                leftSection={<IconBasketDollar size={16} stroke={1} />}
                onClick={open}
            >
                {t('add_price')}
            </Button>

            <CardModal
                opened={opened}
                onClose={close}
                size="fit"
                header={
                    <div className="flex items-center gap-2 font-semibold">
                        <IconBasketDollar size={20} stroke={1.5} />
                        <span>{t('add_price')}</span>
                    </div>
                }
                footer={
                    <div className="flex justify-between gap-2">
                        <Button
                            color="black"
                            variant="subtle"
                            size="xs"
                            onClick={close}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            color="black"
                            size="xs"
                            radius="xl"
                            loading={createPriceMutation.isPending}
                            onClick={save}
                            disabled={!draft?.start_day || !draft?.end_day || draft?.default_amount === null || draft?.default_amount === undefined}
                        >
                            {t('save')}
                        </Button>
                    </div>
                }
            >
                <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <DatePickerInput
                            label={t('start_date')}
                            placeholder={t('select_date')}
                            leftSection={<IconCalendar size={16} stroke={1.5} />}
                            value={draft.start_day ? dayjs(draft.start_day).toDate() : null}
                            onChange={(date) => {
                                const formatted = date ? dayjs(date).format('YYYY-MM-DD') : '';
                                setDraft((prev) => ({
                                    ...prev,
                                    start_day: formatted,
                                    end_day: prev.end_day && formatted && dayjs(prev.end_day).isBefore(dayjs(formatted), 'day') ? '' : prev.end_day,
                                }));
                            }}
                            clearable
                            valueFormat="DD MMMM, YYYY"
                            locale={i18n.language}
                            popoverProps={{ withinPortal: true }}
                        />
                        <DatePickerInput
                            label={t('end_date')}
                            placeholder={t('select_date')}
                            leftSection={<IconCalendar size={16} stroke={1.5} />}
                            value={draft.end_day ? dayjs(draft.end_day).toDate() : null}
                            minDate={draft.start_day ? dayjs(draft.start_day).toDate() : undefined}
                            onChange={(date) => {
                                setDraft((prev) => ({
                                    ...prev,
                                    end_day: date ? dayjs(date).format('YYYY-MM-DD') : '',
                                }));
                            }}
                            clearable
                            valueFormat="DD MMMM, YYYY"
                            locale={i18n.language}
                            popoverProps={{ withinPortal: true }}
                        />
                    </div>

                    <CurrencySelector
                        value={draft?.default_currency || DEFAULT_CURRENCY}
                        onChange={currency => setDraft(prev => ({
                            ...prev,
                            default_currency: currency,
                            child_currency: currency,
                            senior_currency: currency,
                            infant_currency: currency,
                        }))}
                    />

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <MoneyNumberInput
                            amount={draft?.default_amount}
                            onChange={amount => setDraft(prev => ({ ...prev, default_amount: amount }))}
                            label={t('price') + ' * ' + t('default')}
                        />
                        <MoneyNumberInput
                            amount={draft?.child_amount}
                            onChange={amount => setDraft(prev => ({ ...prev, child_amount: amount }))}
                            label={t('children')}
                        />
                        <MoneyNumberInput
                            amount={draft?.senior_amount}
                            onChange={amount => setDraft(prev => ({ ...prev, senior_amount: amount }))}
                            label={service.show_retired ? t('retired_plural') : t('seniors')}
                        />
                        <MoneyNumberInput
                            amount={draft?.infant_amount}
                            onChange={amount => setDraft(prev => ({ ...prev, infant_amount: amount }))}
                            label={t('infants')}
                        />
                    </div>

                    {showForeignerPrice ? (
                        <div className="solid-box-border-blue space-y-2">
                            <div className="font-medium text-sm">
                                {t('foreigner_price')}
                            </div>
                            <CurrencySelector
                                value={draft?.foreigner_currency || DEFAULT_CURRENCY}
                                onChange={currency => setDraft(prev => ({
                                    ...prev,
                                    foreigner_currency: currency,
                                }))}
                            />
                            <MoneyNumberInput
                                className="w-full"
                                amount={draft?.foreigner_amount}
                                onChange={amount => setDraft(prev => ({ ...prev, foreigner_amount: amount }))}
                                label={t('price') + ' * ' + t('foreigner')}
                            />
                        </div>
                    ) : (
                        <div>
                            <Button
                                variant="subtle"
                                size="xs"
                                radius="xl"
                                leftSection={<IconPlus size={14} />}
                                onClick={() => setShowForeignerPrice(true)}
                            >
                                {t('enter_different_foreigner_price')}
                            </Button>
                        </div>
                    )}

                    {Boolean(service.linked_service_id) && (
                        <div className="solid-box-border">
                            <Switch
                                color="black"
                                checked={applyToLinkedService}
                                onChange={(e) => setApplyToLinkedService(e.currentTarget.checked)}
                                label={t('save_to_linked_service')}
                                description={t('do_you_want_to_apply_this_price_to_linked_service')}
                            />
                        </div>
                    )}
                </div>
            </CardModal>
        </>
    );
}